/**
 * FilePreviewOverlay: 회의자료 파일 미리보기 오버레이
 *
 * luna-rocket LUXFileViewer_R 컴포넌트를 활용하여 파일 미리보기를 표시합니다.
 * - OFFICE(hwp, docx, pdf 등): 서버 변환 API → 페이지별 이미지 렌더링
 * - IMAGE(png, jpg 등): 이미지 URL 직접 렌더링
 *
 * LUXFileViewer_R의 내부 API 호출을 `getData` prop으로 오버라이드하여
 * wehago-conference2 자체 ObjectStorage API를 사용합니다.
 */
import React, { useCallback, useMemo } from 'react';
import { useAppSelector } from '@/store/hooks';
import type { MaterialListItem } from '@/api';
import apiManager from '@/api';
import { previewFilesUnauthorized } from '@/utils/objectStorage';
import { serialize } from '@/utils/urlUtils';
import { LUXFileViewer_R } from '@/components/FileViewer';
import { formatFileSize } from '@/utils/formatUtils';

const IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'heic', 'tif', 'tiff'];

function getExt(fileName: string): string {
  const m = fileName.match(/\.([^.]+)$/);
  return m ? m[1].toLowerCase() : '';
}

function getMimeType(ext: string): string {
  const map: Record<string, string> = {
    hwp: 'application/x-hwp', hwpx: 'application/x-hwpx',
    doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    rtf: 'application/rtf',
    xls: 'application/vnd.ms-excel', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    cell: 'application/vnd.ms-excel',
    ppt: 'application/vnd.ms-powerpoint', pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    show: 'application/vnd.ms-powerpoint',
    txt: 'text/plain', one: 'application/onenote', onex: 'application/onenote',
    pdf: 'application/pdf',
    png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
    gif: 'image/gif', bmp: 'image/bmp', webp: 'image/webp',
    tif: 'image/tiff', tiff: 'image/tiff', heic: 'image/heic',
  };
  return map[ext] || 'application/octet-stream';
}

/** fetch Response를 모방하는 래퍼 (LUXFileViewer_R 내부에서 response.json() 호출) */
function fakeResponse<T>(data: T): { json: () => Promise<T> } {
  return { json: () => Promise.resolve(data) };
}

interface FilePreviewOverlayProps {
  material: MaterialListItem;
  onClose: () => void;
}

const FilePreviewOverlay: React.FC<FilePreviewOverlayProps> = ({ material, onClose }) => {
  const ext = getExt(material.fileName);
  const isImage = IMAGE_EXTS.includes(ext);

  /** LUXFileViewer_R fileInfo prop */
  const luxFileInfo = useMemo(() => {
    const nameWithoutExt = material.fileName.replace(/\.[^.]+$/, '');
    const size = material.fileInfo?.fileSize
      ? formatFileSize(material.fileInfo.fileSize)
      : '0KB';

    return {
      name: nameWithoutExt,
      type: ext,
      size,
      mimeType: isImage ? ('IMAGE' as const) : ('OFFICE' as const),
    };
  }, [material, ext, isImage]);

  /** 방 개설자의 cno를 가져옵니다 (비회원 폴백용) */
  const fallbackCno = useAppSelector((s) => s.local.creatorInfo?.company_no || s.local.companyNo || '');
  const cno = material.fileInfo?.cno || apiManager.selectedCno || String(fallbackCno) || '';

  /** LUXFileViewer_R storageInfo prop (getData 오버라이드 시 직접 사용되지 않지만 필수 prop) */
  const luxStorageInfo = useMemo(() => ({
    FileName: material.fileInfo?.newFileName ?? '',
    BucketType: material.fileInfo?.bucketType ?? 'S',
    cno,
  }), [material.fileInfo, cno]);

  /**
   * getData 오버라이드:
   * LUXFileViewer_R 내부에서 OFFICE → API.getData, IMAGE → imgAPI.getImageData 대신 호출.
   * Promise<{ json(): Promise<data> }> 형태로 반환해야 함.
   */
  const handleGetData = useCallback(() => {
    if (!material.fileInfo) {
      return Promise.resolve(fakeResponse({ resultCode: 'ERROR' }));
    }
    const hasToken = !!apiManager.token;

    if (isImage) {
      if (hasToken) {
        // 회원 (token O): getDocumentDetail (services/common) 사용
        return apiManager.getVideoServiceKey().then((result) => {
          if (!result || !result.service_code) {
            throw new Error('Invalid service key result');
          }
          const data = serialize({
            cno,
            FileName: material.fileInfo!.newFileName,
            Ext: ext,
            ServiceCode: result.service_code,
            ServiceKey: result.service_key,
            BucketType: material.fileInfo!.bucketType,
            'Content-Type': getMimeType(ext),
            isWedrive: 'false',
            isFullPreview: 'true',
          });
          return apiManager.getDocumentDetail(data, 'getImageURL');
        }).then((documentData) => {
          if (documentData.data.resultCode === '0000') {
            const resources = (documentData.data.resultList as string[]).map((url) =>
              url.replace('http://dev.api0.wehago.com/', 'https://rtctest.wehago.com/api0/'),
            );
            return fakeResponse({ resultCode: '0000', resultList: resources });
          }
          throw new Error('DocumentDetail returned error: ' + documentData.data.resultCode);
        }).catch((e) => {
          console.warn('[FilePreviewOverlay] Member flow failed (session may be expired), falling back to guest flow:', e);
          // Fallback to guest (NoToken API)
          return previewFilesUnauthorized({
            FileName: material.fileInfo!.newFileName,
            BucketType: material.fileInfo!.bucketType,
            cno: cno || undefined,
            Ext: ext,
            contentType: getMimeType(ext),
            forceNoToken: true,
          }).then((result: any) => {
            if (result && result.resultCode === '0000' && result.resultList && result.resultList.length > 0) {
              const resources = (result.resultList as string[]).map((url) =>
                url.replace('http://dev.api0.wehago.com/', 'https://rtctest.wehago.com/api0/'),
              );
              return fakeResponse({ resultCode: '0000', resultList: resources });
            }
            return fakeResponse({ resultCode: 'E2021' });
          }).catch(() => fakeResponse({ resultCode: 'ERROR' }));
        });
      } else {
        // 비회원 (token X): previewFilesUnauthorized (services/login > getImageURLNoToken) 사용
        return previewFilesUnauthorized({
          FileName: material.fileInfo!.newFileName,
          BucketType: material.fileInfo!.bucketType,
          cno: cno || undefined,
          Ext: ext,
          contentType: getMimeType(ext),
        }).then((result: any) => {
          if (result && result.resultCode === '0000' && result.resultList && result.resultList.length > 0) {
            const resources = (result.resultList as string[]).map((url) =>
              url.replace('http://dev.api0.wehago.com/', 'https://rtctest.wehago.com/api0/'),
            );
            return fakeResponse({ resultCode: '0000', resultList: resources });
          }
          return fakeResponse({ resultCode: 'E2021' });
        }).catch(() => fakeResponse({ resultCode: 'ERROR' }));
      }
    }

    // OFFICE / PDF: 기존과 동일하게 previewFilesUnauthorized 사용
    return previewFilesUnauthorized({
      FileName: material.fileInfo.newFileName,
      BucketType: material.fileInfo.bucketType,
      cno: cno || undefined,
      Ext: ext,
      contentType: getMimeType(ext),
    }).then((result: any) => {
      console.log('[FilePreviewOverlay] Office previewFilesUnauthorized result:', result);
      if (!result || result.resultCode !== '0000') {
        console.warn('[FilePreviewOverlay] Office preview failed or resultCode not 0000', result);
        return fakeResponse({ resultCode: 'E2021' });
      }

      if (result.resultList?.[0]?.resources?.length) {
        const resources = result.resultList[0].resources.map((url: string) =>
          url.replace('http://dev.api0.wehago.com/', 'https://rtctest.wehago.com/api0/'),
        );
        return fakeResponse({
          resultCode: '0000',
          resultList: [{ resources, isFullPreview: true }],
        });
      }

      console.warn('[FilePreviewOverlay] Office preview missing resources array', result);
      return fakeResponse({ resultCode: 'E2021' });
    }).catch((e) => {
      console.error('[FilePreviewOverlay] Office preview caught error:', e);
      return fakeResponse({ resultCode: 'ERROR' });
    });
  }, [material.fileInfo, isImage, ext]);

  const handleAction = useCallback((type: string) => {
    if (type === 'downLoad' && material.fileInfo) {
      import('@/utils/objectStorage').then(({ downloadObjectStorageFile }) => {
        downloadObjectStorageFile({
          FileName: material.fileInfo!.newFileName,
          BucketType: material.fileInfo!.bucketType,
          realFileName: material.fileName,
          cno, // Use computed cno from outer scope
        });
      });
    }
  }, [material, cno]);

  return (
    <LUXFileViewer_R
      fileInfo={luxFileInfo}
      storageInfo={luxStorageInfo}
      authority="read"
      onClose={onClose}
      onAction={handleAction}
      getData={handleGetData}
      useOpenButton={false}
      isONEAIEnabled={false}
    />
  );
};

export default FilePreviewOverlay;
